import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import type { RetryPolicy } from '~/backend/network-retry';
import { QueryOperation } from './connector-next-components';

export type ConnectorNextContext = {
    fetcherOptions: {
        /**
         * Headers to inject in the fetcher
         */
        headers?: Record<string, string>;
        /**
         * Query params to inject in the fetcher
         */
        queryParams?: Record<string, unknown>;
        /**
         * Overrides `defaultRetryPolicy` (network-retry.ts) for this request.
         * Pass 'transient' where the default is 'none' only after
         * establishing that the call site's handling of the response
         * tolerates a duplicate delivery.
         */
        retryPolicy?: RetryPolicy;
        /**
         * Shortens how long a retried request keeps retrying (see
         * `RETRY_WINDOW_MS`). A caller that bounds the request with its own
         * deadline sets this to end well inside it, so the last real answer
         * — say a 503 — is reported rather than cut off as an expiry.
         */
        retryWindowMs?: number;
        /**
         * How long one attempt may wait for its response headers (see
         * `DEFAULT_RESPONSE_TIMEOUT_MS`); expiry counts as a transient
         * failure. A caller that bounds the request with its own deadline
         * sets this well inside it, so an attempt stuck on a dead socket
         * is retried within the deadline instead of consuming it.
         */
        responseTimeoutMs?: number;
    };
    queryOptions: {
        /**
         * Set this to `false` to disable automatic refetching when the query mounts or changes query keys.
         * Defaults to `true`.
         */
        enabled?: boolean;
    };
    /**
     * Query key manager.
     */
    queryKeyFn: (operation: QueryOperation) => QueryKey;
};

/**
 * Context injected into every react-query hook wrappers
 *
 * @param queryOptions options from the useQuery wrapper
 */
export function useConnectorNextContext<
    TQueryFnData = unknown,
    TError = unknown,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    _queryOptions?: Omit<
        UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
        'queryKey' | 'queryFn'
    >
): ConnectorNextContext {
    return {
        fetcherOptions: {},
        queryOptions: {},
        queryKeyFn,
    };
}

export const queryKeyFn = (operation: QueryOperation) => {
    const queryKey: unknown[] = hasPathParams(operation)
        ? operation.path
              .split('/')
              .filter(Boolean)
              .map((i) => resolvePathParam(i, operation.variables.pathParams))
        : operation.path.split('/').filter(Boolean);

    if (hasQueryParams(operation)) {
        queryKey.push(operation.variables.queryParams);
    }

    if (hasBody(operation)) {
        queryKey.push(operation.variables.body);
    }

    return queryKey;
};
// Helpers
// Substitutes placeholders anywhere within a segment (like the fetcher's URL
// building). A whole-segment check would miss `{seriesUid}.zip`/`{sopUid}.dcm`,
// leaving the id literal so two series/instances would share one cache key.
const resolvePathParam = (key: string, pathParams: Record<string, string>) =>
    key.replace(/\{(\w+)\}/g, (_match, name: string) => pathParams[name]);

const hasPathParams = (
    operation: QueryOperation
): operation is QueryOperation & {
    variables: { pathParams: Record<string, string> };
} => {
    return Boolean(
        (operation.variables as { pathParams?: unknown }).pathParams
    );
};

const hasBody = (
    operation: QueryOperation
): operation is QueryOperation & {
    variables: { body: Record<string, unknown> };
} => {
    return Boolean((operation.variables as { body?: unknown }).body);
};

const hasQueryParams = (
    operation: QueryOperation
): operation is QueryOperation & {
    variables: { queryParams: Record<string, unknown> };
} => {
    return Boolean(
        (operation.variables as { queryParams?: unknown }).queryParams
    );
};
